import React, { useState } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { useCurrencies, useQueryParams } from "../../utils/hooks";
import { useQuery } from "react-query";
import { useAuth } from "../../hooks/useAuth";

import queryString from "query-string";
import ReactPaginate from "react-paginate";
import { paginationOptions } from "../../utils/helpers";
import { LoaderIcon, Logo } from "../Icons";
import currency from "currency.js";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

function groupItemsByCategory(items) {
  const groupedData = {};
  const allCategories = {};

  items.forEach((item) => {
    // Get top category
    const topCategoryKey = item?.topCategory
      ? item?.topCategory?.toLowerCase()?.replace(/\s+/g, "_")
      : "all";

    // Get category key
    const categoryKey = item?.category
      ? item?.category?.toLowerCase()?.replace(/\s+/g, "_")
      : "all";

    // Initialize top category if it doesn't exist
    if (!groupedData[topCategoryKey]) {
      groupedData[topCategoryKey] = {};
    }

    // Initialize category under top category if it doesn't exist
    if (!groupedData[topCategoryKey][categoryKey]) {
      groupedData[topCategoryKey][categoryKey] = [];
    }

    const itemData = {
      name: item.title,
      price: item.price,
      barCode: item.Bar_Code,
      topCategory: item?.topCategory || "",
      description: item?.description,
      images: item?.images,
      category: item?.category,
    };

    // Push item to the category
    groupedData[topCategoryKey][categoryKey].push(itemData);

    // Also add to allCategories for the "All" top category
    if (!allCategories[categoryKey]) {
      allCategories[categoryKey] = [];
    }
    allCategories[categoryKey].push(itemData);
  });

  // Create ordered data with "All" appearing first
  const orderedData = {};

  // Add "All" top category with all categories
  if (Object.keys(allCategories).length > 0) {
    orderedData.all = allCategories;
  }

  // Add other categories
  Object.keys(groupedData).forEach((key) => {
    if (key !== "all") {
      orderedData[key] = groupedData[key];
    }
  });

  return orderedData;
}

const MenuCard = ({ item }) => {
  const { customerBackendUrl: backendUrl } = useAuth();
  const { prevailingCurrencySymbol } = useCurrencies();

  const getImageUrl = () => {
    const image = item?.images ? JSON.parse(item?.images) : [];
    let imageUrl = image[0]?.systemFileName;
    imageUrl = imageUrl
      ? `${backendUrl}/api/items/view-file/${imageUrl}`
      : "https://shop.pointsbud.com/_nuxt/img/spirit-shot.51954ca.png";

    return imageUrl;
  };

  return (
    <>
      <div className="col-12">
        <div
          className="card border-0 shadow-sm h-100 position-relative"
          style={{ transition: "all 0.3s ease" }}
        >
          <div className="card-body p-4">
            <div className="row align-items-start">
              {/* Left Content */}
              <div className="col-8">
                <div className="mb-3">
                  <h5 className="card-title fw-bold text-dark mb-2">
                    {item?.title || ""}
                  </h5>
                  <p className="card-text text-muted  lh-base mb-0">
                    {item?.description || ""}
                    {/* <span
                      className="text-warning ms-1"
                      style={{
                        cursor: "pointer",
                        textDecoration: "none",
                      }}
                    >
                      see more
                    </span> */}
                  </p>
                </div>
                <div className="fs-5 fw-bold text-dark">
                  {currency(item?.price).format({
                    symbol: prevailingCurrencySymbol,
                    separator: ",",
                  })}
                </div>
              </div>

              {/* Right Content - Image and Add Button */}
              <div className="col-4">
                <div className="position-relative">
                  <div
                    className="rounded-3 overflow-hidden  w-100"
                    style={{ height: "250px" }}
                  >
                    <img
                      src={getImageUrl()}
                      alt={"img"}
                      className="img-fluid w-100 h-100"
                      style={{ objectFit: "contain" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
        }

        .text-warning:hover {
          color: #f8960d !important;
        }

        .btn-dark:hover {
          background-color: #495057 !important;
        }

        .btn-warning:hover {
          background-color: #f8960d !important;
          border-color: #f8960d !important;
        }
      `}</style>
    </>
  );
};

const CustomMenuPage = () => {
  const navigate = useNavigate();
  const initialFilterParams = {
    page: 1,
    limit: 150, // Increased to get more items
    isGrouped: true,
  };
  const [queryParams] = useQueryParams({
    page: 1,
    limit: 150,
    ...initialFilterParams,
  });

  const { customerBackendUrl: backendUrl } = useAuth();

  const { data, isFetching } = useQuery(
    [queryParams],
    () => getItems(queryParams),
    {
      onSuccess: (data) => {
        const firstTopCategory = Object.keys(data)[0];
        setTitle(firstTopCategory || "");
        setSelectedTopCategory(firstTopCategory || "");
        setSelectedSubCategory("");
      },
      keepPreviousData: true,
    },
  );

  const [textTitle, setTitle] = useState("");
  const [selectedTopCategory, setSelectedTopCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");

  const handleTopCategoryClick = (topCategory) => {
    setSelectedTopCategory(topCategory);
    setSelectedSubCategory("");
  };

  const handleSubCategoryClick = (subCategory) => {
    setSelectedSubCategory(subCategory);
    const element = document.getElementById(
      `${selectedTopCategory}_${subCategory}`,
    );
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const getSubCategories = () => {
    if (!selectedTopCategory || !data) return [];
    return Object.keys(data[selectedTopCategory] || {});
  };

  const fetchSetUpData = async () => {
    // await waitFor(5000);
    let response = await fetch(`${backendUrl}/api/store/set-up`, {
      method: "GET",
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();

    return data;
  };

  const { data: settings, isLoading } = useQuery(
    ["STORE_NAV_SET_UP"],
    () => fetchSetUpData(),
    {
      keepPreviousData: true,
    },
  );
  if (!isLoading && !settings?.settings?.enableCustomStoreMenu) {
    return (
      <div
        className="vw-100 d-flex align-items-center justify-content-center text-center "
        style={{ height: "60vh" }}
      >
        <div>
          <Logo />
          <h4 className="display-5"> Unauthorized</h4>{" "}
          <Button
            onClick={() => navigate(-1)}
            variant=""
            className="text-primary text-underline"
          >
            Go back
          </Button>
        </div>
      </div>
    );
  }

  const getItems = async (debouncedqueryParams) => {
    let response = await fetch(
      `${backendUrl}/api/store/custom-store-menu-list?${queryString.stringify(
        debouncedqueryParams,
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    let data = await response.json();

    data = groupItemsByCategory(data?.data || []);

    return data;
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: data?.company?.CompName,
        text: `Check out our menu at ${data?.company?.CompName}!`,
        url: window.location.href,
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast.success("Link copied to clipboard!");
      });
    }
  };

  // Loading state
  if (isFetching && !data) {
    return (
      <div className="global-spinner">
        <LoaderIcon className="spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <div className="container-fluid bg-white py-2">
        <div className="row justify-content-center sticky-top bg-white py-4">
          <div className="col-lg-8 ">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="">
                  <img
                    src={`${backendUrl}/api/users/logo/company`}
                    alt=""
                    width={100}
                    height={100}
                  />{" "}
                </div>
                <div
                  className="d-flex flex-column justify-content-between "
                  style={{ height: "100%", justifyContent: "space-between" }}
                >
                  <div className="px-4 ">
                    <h4
                      className=" fw-bold mb-0 text-black"
                      style={{ fontSize: "24px", lineHeight: "1.1" }}
                    >
                      {" "}
                      {settings?.company?.CompName}
                    </h4>
                  </div>

                  <div className="p-4 pb-4   text-black">
                    <div
                      className=""
                      style={{ fontSize: "14px", lineHeight: "1.4" }}
                    >
                      <div className="mb-1">
                        {" "}
                        {settings?.company?.AddressLine1}
                      </div>
                      <div className="mb-1">
                        {settings?.company?.AddressLine2}
                      </div>
                      <div>{settings?.company?.Phone}</div>
                    </div>
                  </div>
                </div>
              </div>
              <h3>Menu</h3>
            </div>
            {data &&
              Object.keys(data).map((topCategory) => (
                <button
                  onClick={() => handleTopCategoryClick(topCategory)}
                  className="p-2 px-4 fw-bold mx-3 mb-3"
                  key={topCategory}
                  style={{
                    border: "1px solid black",
                    textTransform: "capitalize",
                    borderRadius: "40px",
                    backgroundColor:
                      topCategory === selectedTopCategory
                        ? "black"
                        : "transparent",
                    color:
                      topCategory === selectedTopCategory ? "white" : "black",
                    cursor: "pointer",
                  }}
                >
                  {topCategory.replace(/_/g, " ")}
                </button>
              ))}

            {/* Sub-category buttons */}
            {selectedTopCategory && getSubCategories().length > 0 && (
              <div className="mt-3">
                <div className="mb-2">
                  <small className="text-muted">Select Category:</small>
                </div>
                {getSubCategories().map((subCategory) => (
                  <button
                    onClick={() => handleSubCategoryClick(subCategory)}
                    className="p-2 px-3 fw-bold mx-2 mb-3"
                    key={subCategory}
                    style={{
                      border: "1px solid #fd7e14",
                      borderRadius: "40px",
                      backgroundColor:
                        subCategory === selectedSubCategory
                          ? "#fd7e14"
                          : "transparent",
                      color:
                        subCategory === selectedSubCategory
                          ? "white"
                          : "#fd7e14",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    {subCategory.replace(/_/g, " ")}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {data &&
          Object.entries(data).map(([topCategory, subCategories]) => {
            // Show all sections if no specific sub-category is selected, or if it's the "all" top category
            // Otherwise, only show the selected sub-category
            if (
              selectedSubCategory &&
              selectedTopCategory !== "all" &&
              topCategory === selectedTopCategory
            ) {
              return (
                <div key={topCategory}>
                  {/* Sub-categories and items */}
                  {Object.entries(subCategories)
                    .filter(
                      ([subCategory]) => subCategory === selectedSubCategory,
                    )
                    .map(([subCategory, items]) => (
                      <div
                        className="row justify-content-center"
                        key={subCategory}
                        id={`${topCategory}_${subCategory}`}
                        style={{ scrollMarginTop: "400px" }}
                      >
                        <div className="col-lg-8">
                          {/* Sub-category Header */}

                          <div className="text-center mt-5 mb-4">
                            <h3 className="fw-bold text-dark mb-3">
                              {subCategory.replace(/_/g, " ")}
                            </h3>
                            <div
                              className="mx-auto"
                              style={{
                                width: "60px",
                                height: "2px",
                                backgroundColor: "#fd7e14",
                              }}
                            />
                          </div>

                          <style jsx>{`
                            @media (max-width: 768px) {
                              h2 {
                                font-size: 28px !important;
                              }
                              h3 {
                                font-size: 20px !important;
                              }
                            }
                          `}</style>

                          {/* Menu Items */}
                          {Array.isArray(items) &&
                            items.map((item, index) => (
                              <div className="row g-4 mb-3" key={index}>
                                <MenuCard item={item} />
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                </div>
              );
            }

            // Show all sub-categories when "All" is selected or no specific sub-category selected
            if (!selectedSubCategory || selectedTopCategory === "all") {
              return (
                <div key={topCategory}>
                  {/* Sub-categories and items */}
                  {Object.entries(subCategories).map(([subCategory, items]) => (
                    <div
                      className="row justify-content-center"
                      key={subCategory}
                      id={`${topCategory}_${subCategory}`}
                      style={{ scrollMarginTop: "400px" }}
                    >
                      <div className="col-lg-8">
                        {/* Sub-category Header */}

                        <div className="text-center mt-5 mb-4">
                          <h3 className="fw-bold text-dark mb-3">
                            {subCategory.replace(/_/g, " ")}
                          </h3>
                          <div
                            className="mx-auto"
                            style={{
                              width: "60px",
                              height: "2px",
                              backgroundColor: "#fd7e14",
                            }}
                          />
                        </div>

                        <style jsx>{`
                          @media (max-width: 768px) {
                            h2 {
                              font-size: 28px !important;
                            }
                            h3 {
                              font-size: 20px !important;
                            }
                          }
                        `}</style>

                        {/* Menu Items */}
                        {Array.isArray(items) &&
                          items.map((item, index) => (
                            <div className="row g-4 mb-3" key={index}>
                              <MenuCard item={item} />
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            }

            return null;
          })}

        {/* <div className="text-center mt-5">
          <button className="btn btn-warning btn-lg px-5 py-3 fw-semibold">
            View Cart
          </button>
        </div> */}
      </div>
    </>
  );
};

export default CustomMenuPage;
